(() => {
  'use strict';
  const LIGHT = '#E5E3DE', DARK = '#303433';
  let cleanup;
  const init = () => {
    cleanup?.();
    const header = document.querySelector('.header-wrapper'), stack = header?.closest('.obj-header-stack');
    if (!header || !stack) return;
    const abort = new AbortController(), options = {signal: abort.signal};
    const listen = (target, type, callback, extra = {}) => target.addEventListener(type, callback, {...extra, ...options});
    const root = document.documentElement;
    const visible = new Map();
    let observer, regions = [], frame = 0, activeColour = '', observerBounds = '';
    const themeContrast = () => root.dataset.colorMode === 'dark' ? 'white' : 'black';
    const contrastFor = element => {
      const value = (root.dataset.colorMode === 'dark' && element?.dataset.headerContrastDark) || element?.dataset.headerContrast;
      return value === 'white' || value === 'black' ? value : themeContrast();
    };
    const apply = (contrast, source) => {
      const colour = contrast === 'white' ? LIGHT : DARK;
      if (colour !== activeColour) {
        activeColour = colour;
        root.style.setProperty('--obj-header-fg', colour);
        root.dataset.headerContrast = contrast;
      }
      if (source === 'alpha-fill') root.dataset.headerContrastSource = source;
      else delete root.dataset.headerContrastSource;
    };
    const update = () => {
      if (document.hidden || abort.signal.aborted) return;
      const modal = document.querySelector('dialog:modal');
      if (modal && modal.id !== 'CartDrawer') { apply(contrastFor(modal)); return; }
      const ranges = regions.filter(element => visible.has(element)).map(element => ({element, ...visible.get(element)}));
      const edges = [...new Set([0, innerWidth, ...ranges.flatMap(range => [range.left, range.right])])].sort((a, b) => a - b);
      let white = 0, black = 0, source, largest = 0;
      for (let i = 1; i < edges.length; i++) {
        const midpoint = (edges[i - 1] + edges[i]) / 2, width = edges[i] - edges[i - 1];
        // Nested declarations replace their parent only across their own width.
        const region = ranges.findLast(range => range.left <= midpoint && range.right >= midpoint)?.element;
        if (contrastFor(region) === 'white') white += width;
        else black += width;
        if (width > largest) { largest = width; source = region; }
      }
      const alpha = source?.matches('.obj-scrolly-section, [data-box-section]') && source.dataset.alphaMode === root.dataset.colorMode;
      apply(white === black ? themeContrast() : white > black ? 'white' : 'black', alpha ? 'alpha-fill' : undefined);
    };
    const syncHeight = (refreshRegions = false) => {
      frame = 0;
      if (abort.signal.aborted || document.hidden) return;
      const bounds = stack.getBoundingClientRect();
      if (bounds.bottom > 0) {
        const height = `${bounds.bottom}px`;
        if (root.style.getPropertyValue('--obj-nav-height') !== height) root.style.setProperty('--obj-nav-height', height);
      }
      const y = Math.max(0, Math.min(innerHeight - 1, Math.round(bounds.top + bounds.height / 2)));
      const key = `${innerWidth}:${innerHeight}:${y}`;
      if (refreshRegions || key !== observerBounds) {
        observer?.disconnect();
        visible.clear();
        observerBounds = key;
        regions = [...document.querySelectorAll('[data-header-contrast]')].filter(element =>
          element !== root && element !== document.body && !element.closest('.obj-header-stack, dialog'));
        observer = new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRect.width > 0) {
              visible.set(entry.target, {left: Math.max(0, entry.intersectionRect.left), right: Math.min(innerWidth, entry.intersectionRect.right)});
            } else visible.delete(entry.target);
          }
          update();
        }, {rootMargin: `${-y}px 0px ${-(innerHeight - y - 1)}px 0px`, threshold: 0});
        for (const region of regions) observer.observe(region);
      }
      update();
    };
    const schedule = () => {
      if (!frame && !document.hidden) frame = requestAnimationFrame(() => syncHeight());
    };
    const refresh = () => syncHeight(true);
    window.ObjectivHeaderContrast = {refresh};
    // Keep the mobile menu above fixed content, and behind the cart/loader/offer ticket.
    const syncTopLayer = () => {
      if (stack.showPopover) {
        if (document.querySelector('[data-objectiv-loader][open], dialog#CartDrawer:modal, [data-offer-ticket-dialog]:modal')) {
          if (stack.matches(':popover-open')) stack.hidePopover();
          stack.removeAttribute('popover');
        } else {
          const modal = document.querySelector('dialog:modal');
          const menuOpen = header.querySelector('header-drawer > details[open]');
          if (modal || menuOpen) {
            stack.setAttribute('popover', 'manual');
            if (modal && stack.matches(':popover-open')) stack.hidePopover();
            if (!stack.matches(':popover-open')) stack.showPopover();
          } else if (stack.hasAttribute('popover')) {
            if (stack.matches(':popover-open')) stack.hidePopover();
            stack.removeAttribute('popover');
          }
        }
      }
      update();
      schedule();
    };
    const overlayObserver = new MutationObserver(records => {
      if (records.some(record => record.attributeName === 'open')) syncTopLayer();
      if (records.some(record => record.attributeName.startsWith('data-header-contrast'))) refresh();
      else update();
    });
    overlayObserver.observe(document.body, {subtree: true, attributes: true, attributeFilter: ['open', 'data-header-contrast', 'data-header-contrast-dark', 'data-alpha-mode']});
    listen(document, 'objectiv:menu-change', syncTopLayer);
    listen(window, 'objectiv:loader-dismissed', syncTopLayer);
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(stack);
    for (const img of header.querySelectorAll('img.objectiv-header-logo, .obj-journal-wordmark-wrapper img')) {
      img.parentElement.classList.add('obj-header-logo-mask');
      img.parentElement.style.setProperty('--obj-header-mask', `url(${JSON.stringify(img.currentSrc || img.src)})`);
    }
    listen(window, 'resize', schedule, {passive: true});
    listen(window, 'pageshow', refresh);
    listen(document, 'visibilitychange', refresh);
    listen(document, 'objectiv:color-mode-change', update);
    listen(header, 'toggle', syncTopLayer, {capture: true});
    document.fonts?.ready.then(() => { if (!abort.signal.aborted) schedule(); });
    syncHeight(true);
    syncTopLayer();
    cleanup = () => {
      abort.abort(); resizeObserver.disconnect(); overlayObserver.disconnect(); observer?.disconnect();
      cancelAnimationFrame(frame);
      if (stack.matches(':popover-open')) stack.hidePopover();
      stack.removeAttribute('popover');
      visible.clear();
      delete window.ObjectivHeaderContrast;
    };
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true}); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches('.header-wrapper') || event.target.querySelector('.header-wrapper')) cleanup?.();
    else init();
  });
})();
